import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
    Alert_,
    AlertContext,
    Checkbox_,
    Filters_,
    Grey_Link,
    Input_,
    Main_Plain,
    PageAlwaysVisible,
    PageFull,
    PageSidebar,
    PageTitle,
    Partial_Loader,
    Slider_,
    Table,
    TD,
    TH,
    Toggle_Stat,
    TR
} from 'monica-alexandria';
import { alertMessage } from '../../../helpers/messagesAlerts';
import { Toolbar_ } from '../../partials/navigation/Toolbar_.jsx';
import { read_dolores_growth_users } from '../../../services/api/HumanResources';
import { assign_brand_seller, edit_prospect, get_prospect_filter_options, get_prospects, search_prospects } from '../../../services/api/Prospects';
import { checkPermissions } from '../../../helpers/checkPermissions';
import store from '../../../redux/store';
import Consolidate_Brands from '../../partials/merge/Consolidate_Brands';
import Merge_Records from '../../partials/merge/Merge_Records';
import { Leads_Brand } from './partials/Leads_Brand';
import { Leads_Info } from './partials/Leads_Info';
import {
    defaultLeadFilters,
    Lead_Filters,
    read_active_filters_count,
    read_lead_filters
} from './components/Lead_Filters';
import { read_prospect_sass_summary, read_registered_status } from './lead_table_helpers';
import { Quest_Provider_Counts } from '../../../components/quest/Quest_Provider_Counts';
import { read_seller_identity_keys, read_seller_payload } from '../../../utils/hr/seller_identity';
import { Leads_Combined_Timeline } from './components/Leads_Combined_Timeline';

const LeadPage = styled.div``;

const LeadPageContent = styled.div`
    display: grid;
    gap: 2rem;
`;

const LeadPageActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`;

const LeadPageTags = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

const LeadPageTag = styled.div`
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    background: ${p => p.backgroundColor};

    h5 {
        white-space: nowrap;
        color: ${p => p.theme.color};
    }
`;

const LeadRegistrationStatus = styled.span`
    display: inline-flex;
    align-items: center;
    padding: var(--smallPads);
    border: 0.1rem solid transparent;
    border-radius: var(--smallRadius);
    font-weight: 700;
    color: ${p => p.$registered ? 'var(--success)' : 'var(--error)'};
    background: ${p => p.$registered
        ? 'color-mix(in srgb, var(--success) 12%, transparent)'
        : 'color-mix(in srgb, var(--error) 12%, transparent)'};
    border-color: ${p => p.$registered
        ? 'color-mix(in srgb, var(--success) 32%, transparent)'
        : 'color-mix(in srgb, var(--error) 32%, transparent)'};
`;

const LeadRow = styled(TR)`
    cursor: pointer;

    :hover {
        background: ${p => p.theme.low};
    }
`;

const LeadMergeSelectionCell = styled(TD)`
    width: 4rem;

    p {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
`;

const LeadPageLoadMoreTrigger = styled.div`
    display: flex;
    justify-content: center;
    padding: var(--normalPads);
`;

const LeadMergeActions = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
`;

const pageSize = 30;
const prospectAssignedStatus = 'To Call';

/**
 * Reads the oldest trustworthy creation timestamp for one Lead.
 */
const read_prospect_merge_time = (prospect) => {
    const timestamp = [prospect?.legacyCreatedAt, prospect?.createdAt]
        .map((value) => new Date(value || '').getTime())
        .find(Number.isFinite);

    if (timestamp !== undefined) {
        return timestamp;
    }

    const prospectId = `${prospect?._id || ''}`;

    return /^[a-f\d]{24}$/i.test(prospectId)
        ? Number.parseInt(prospectId.slice(0, 8), 16) * 1000
        : Number.MAX_SAFE_INTEGER;
};

/**
 * Keeps the oldest selected Lead first so it is always the merge survivor.
 */
export const sort_merge_prospects_oldest_first = (prospects = []) => {
    return [...prospects].sort((firstProspect, secondProspect) => {
        const timestampDifference = read_prospect_merge_time(firstProspect) - read_prospect_merge_time(secondProspect);

        if (timestampDifference) {
            return timestampDifference;
        }

        return `${firstProspect?._id || ''}`.localeCompare(`${secondProspect?._id || ''}`);
    });
};

/**
 * Preserves a registered account Lead when one is selected, then uses age as the stable fallback.
 */
export const sort_merge_prospects_for_survivor = (prospects = []) => {
    const orderedProspects = sort_merge_prospects_oldest_first(prospects);
    const registeredProspects = orderedProspects.filter((prospect) => `${prospect?.userId || ''}`.trim());

    return registeredProspects.length
        ? [...registeredProspects, ...orderedProspects.filter((prospect) => !`${prospect?.userId || ''}`.trim())]
        : orderedProspects;
};

const read_merge_prospect_label = (prospect) => {
    return [prospect?.firstName, prospect?.lastName].filter(Boolean).join(' ') || prospect?.username || prospect?._id;
};
/**
 * Creates one stable numeric seed from chip text.
 */
const read_label_seed = (label) => {
    return `${label || ''}`.split('').reduce((total, character, index) => {
        return total + character.charCodeAt(0) * (index + 1);
    }, 0);
};

/**
 * Generates a readable pale background color for repeated values.
 */
const read_tag_background_color = (label) => {
    const seed = read_label_seed(label);
    const hue = seed % 360;
    const saturation = 30 + seed % 10;
    const lightness = 88 + seed % 5;

    return `hsl(${hue} ${saturation}% ${lightness}%)`;
};

/**
 * Reads a source label as the username before the email domain.
 */
const read_source_username = (source) => {
    const sourceValue = `${source || ''}`.trim();

    if (!sourceValue.includes('@')) {
        return sourceValue;
    }

    return sourceValue.split('@')[0].trim();
};

/**
 * Resolves first-name source labels to the matching HR username when possible.
 */
const read_source_label = (source, members) => {
    const sourceValue = read_source_username(source);

    if (/\berised\b/i.test(sourceValue)) return 'Portal';

    const matchedMember = (members || []).find((member) => {
        const firstName = `${member?.firstname || member?.firstName || ''}`.trim();
        const fullName = [member?.firstname || member?.firstName, member?.lastname || member?.lastName].filter(Boolean).join(' ');

        return sourceValue === firstName || sourceValue === fullName;
    });

    if (!matchedMember) {
        return sourceValue;
    }

    return read_source_username(matchedMember?.email || matchedMember?.username) || sourceValue;
};

/**
 * Checks whether the selected prospect contains a new brand without a name.
 */
const read_has_unnamed_draft_brand = (selectedProspect) => {
    return (selectedProspect?.brands || []).some((brand) => !brand?._id && !`${brand?.internalName || brand?.name || ''}`.trim());
};

/**
 * Saves the edited prospect and refreshes the currently selected row.
 */
const save_selected_prospect = async (selectedProspect, savedSnapshot, setSelectedProspect, setProspects, setSlider, alerts, setAlerts, t) => {
    if (read_has_unnamed_draft_brand(selectedProspect)) {
        alertMessage(alerts, setAlerts, t('Brand name is required'), 'Error');
        return;
    }

    if (savedSnapshot === JSON.stringify(selectedProspect)) {
        setSelectedProspect(null);
        setSlider('');
        return;
    }

    let response;

    try {
        response = await edit_prospect(selectedProspect);
    } catch (error) {
        const message = error?.response?.status === 409
            ? 'This prospect changed. Refresh before saving again.'
            : 'There was an error saving prospect';
        alertMessage(alerts, setAlerts, t(message), 'Error');
        return;
    }
    const savedProspect = response?.data?.prospect;

    if (!savedProspect) {
        alertMessage(alerts, setAlerts, t('There was an error saving prospect'), 'Error');
        return;
    }

    setProspects((currentProspects) => currentProspects.map((prospect) => {
        if (prospect?._id !== savedProspect?._id) {
            return prospect;
        }

        return savedProspect;
    }));
    setSelectedProspect(null);
    setSlider('');
    alertMessage(alerts, setAlerts, t('Prospect saved'), 'Success');

    if (response?.data?.sync?.warning) {
        alertMessage(alerts, setAlerts, response.data.sync.warning, 'Warning');
    }
};

export default function LEADS() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();
    const routeLanguage = lang || 'en';
    const { alerts, setAlerts } = useContext(AlertContext);
    const loadMoreTriggerRef = useRef(null);
    const loadingRequestRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const requestSequenceRef = useRef(0);
    const selectedProspectSnapshotRef = useRef('');
    const [sidebar, setSidebar] = useState(true);
    const [prospects, setProspects] = useState([]);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [mergeMode, setMergeMode] = useState(false);
    const [selectedMergeProspectIds, setSelectedMergeProspectIds] = useState([]);
    const [mergeProspects, setMergeProspects] = useState([]);
    const [growthMembers, setGrowthMembers] = useState([]);
    const [sourceOptions, setSourceOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [search] = useDebounce(searchTerm, 500);
    const [pageNo, setPageNo] = useState(1);
    const [totalProspects, setTotalProspects] = useState(0);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [assigningBrandId, setAssigningBrandId] = useState('');
    const [slider, setSlider] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [filters, setFilters] = useState(() => read_lead_filters(defaultLeadFilters));
    const [filterDraft, setFilterDraft] = useState(() => read_lead_filters(defaultLeadFilters));
    const showLoadMore = Boolean(totalProspects) && prospects.length < totalProspects && !search && !slider;
    const activeFiltersCount = read_active_filters_count(filters);
    const filtersButtonText = activeFiltersCount ? `${t('Filters')} (${activeFiltersCount})` : t('Filters');
    const canMerge = checkPermissions('King');
    const selectedMergeProspects = selectedMergeProspectIds.map((prospectId) => {
        return prospects.find((prospect) => `${prospect?._id || ''}` === prospectId);
    }).filter(Boolean);
    const currentUser = store.getState()?.user?.user || {};
    const currentSeller = read_seller_payload({
        ...currentUser,
        userId: currentUser?.userId || currentUser?.sub || '',
        id: currentUser?.userId || currentUser?.sub || ''
    });
    const canAssignToMe = read_seller_identity_keys(currentSeller).length > 0;

    /**
     * Loads one Prospect page into the table.
     */
    const load_prospects = (page, mode) => {
        const requestId = requestSequenceRef.current + 1;

        requestSequenceRef.current = requestId;
        loadingRequestRef.current = true;
        setLoading(true);

        return get_prospects(page, pageSize, filters)
            .then((response) => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                const loadedProspects = response?.prospects || [];

                setProspects((currentProspects) => mode === 'append' ? [...currentProspects, ...loadedProspects] : loadedProspects);
                setTotalProspects(response?.count || 0);
                setPageNo(page + 1);
            })
            .catch(() => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                alertMessage(alerts, setAlerts, t('There was an error loading prospects'), 'Error');
            })
            .finally(() => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                loadingRequestRef.current = false;
                setLoading(false);
            });
    };

    /**
     * Loads more prospects when the table bottom is reached.
     */
    const load_more_prospects = () => {
        if (!showLoadMore || loadingRequestRef.current || loadingMoreRef.current) {
            return;
        }

        loadingMoreRef.current = true;
        setLoadingMore(true);

        load_prospects(pageNo, 'append')
            .finally(() => {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            });
    };

    /**
     * Opens the nested brand editor for the clicked prospect.
     */
    const open_prospect_slider = (prospect) => {
        selectedProspectSnapshotRef.current = JSON.stringify(prospect);
        setSelectedProspect(prospect);
        setSlider('brands');
    };

    /**
     * Closes the nested brand editor without saving draft edits.
     */
    const close_prospect_slider = () => {
        setSelectedProspect(null);
        setSlider('');
    };

    /**
     * Assigns one unowned nested Brand to the current member without closing the Lead slider.
     */
    const assign_selected_brand_to_me = (brand) => {
        const brandId = `${brand?._id || ''}`;

        if (!selectedProspect?._id || !brandId || !canAssignToMe || read_seller_identity_keys(brand?.seller || brand?.assignee || brand?.sellerId).length) {
            return;
        }

        setAssigningBrandId(brandId);

        assign_brand_seller({
            ...brand,
            prospectId: selectedProspect._id,
            brandId
        }, currentSeller, prospectAssignedStatus)
            .then((updatedProspect) => {
                if (updatedProspect?.blocked) {
                    alertMessage(alerts, setAlerts, t(updatedProspect?.message || updatedProspect?.msg || 'There was an error assigning seller'), 'Error');
                    return;
                }

                if (!updatedProspect?._id) {
                    alertMessage(alerts, setAlerts, t('There was an error assigning seller'), 'Error');
                    return;
                }

                selectedProspectSnapshotRef.current = JSON.stringify(updatedProspect);
                setSelectedProspect(updatedProspect);
                setProspects((currentProspects) => currentProspects.map((prospect) => {
                    return prospect?._id === updatedProspect._id ? updatedProspect : prospect;
                }));
                alertMessage(alerts, setAlerts, t('Seller assigned'), 'Success');
            })
            .catch(() => {
                alertMessage(alerts, setAlerts, t('There was an error assigning seller'), 'Error');
            })
            .finally(() => {
                setAssigningBrandId('');
            });
    };

    const open_consolidate_brands_slider = () => {
        if (selectedProspectSnapshotRef.current !== JSON.stringify(selectedProspect)) {
            alertMessage(alerts, setAlerts, t('Save your changes before consolidating brands.'), 'Warning');
            return;
        }
        setSlider('consolidate-brands');
    };

    /**
     * Shows or hides the King-only merge-selection column.
     */
    const toggle_merge_mode = () => {
        setMergeMode((currentMode) => {
            if (currentMode) {
                setSelectedMergeProspectIds([]);
            }

            return !currentMode;
        });
    };

    /**
     * Adds or removes one Lead from the current unrestricted merge selection.
     */
    const toggle_merge_prospect = (prospectId) => {
        const prospectKey = `${prospectId || ''}`;

        if (!prospectKey) {
            return;
        }

        setSelectedMergeProspectIds((currentIds) => currentIds.includes(prospectKey)
            ? currentIds.filter((currentId) => currentId !== prospectKey)
            : [...currentIds, prospectKey]);
    };

    /**
     * Opens conflict review with account identity, then age, deciding the fixed survivor.
     */
    const open_selected_merge_slider = () => {
        if (selectedMergeProspects.length < 2) {
            return;
        }

        setMergeProspects(sort_merge_prospects_for_survivor(selectedMergeProspects));
        setSlider('merge-selected');
    };

    const close_selected_merge_slider = () => {
        setMergeProspects([]);
        setSlider('');
    };

    const apply_selected_merge = () => {
        setSelectedMergeProspectIds([]);
        setMergeProspects([]);
        setMergeMode(false);
        setSlider('');
        setRefreshKey((value) => value + 1);
        alertMessage(alerts, setAlerts, t('Leads merged'), 'Success');
    };

    /**
     * Opens the Leads filter slider with the current applied filters.
     */
    const open_filters_slider = () => {
        setFilterDraft(read_lead_filters(filters));
        setSlider('filters');
    };

    /**
     * Closes the Leads filter slider without applying draft values.
     */
    const close_filters_slider = () => {
        setSlider('');
    };

    /**
     * Applies the draft filters and reloads the Leads table.
     */
    const apply_filters = () => {
        setFilters(read_lead_filters(filterDraft));
        setSlider('');
    };

    /**
     * Clears filter state and reloads the Leads table.
     */
    const clear_filters = () => {
        setFilterDraft(read_lead_filters(defaultLeadFilters));
        setFilters(read_lead_filters(defaultLeadFilters));
        setSlider('');
    };

    useEffect(() => {
        if (search) {
            const requestId = requestSequenceRef.current + 1;

            requestSequenceRef.current = requestId;
            loadingRequestRef.current = true;
            setLoading(true);

            search_prospects(search, filters)
                .then((loadedProspects) => {
                    if (requestId !== requestSequenceRef.current) {
                        return;
                    }

                    setProspects(loadedProspects || []);
                    setTotalProspects(loadedProspects?.length || 0);
                })
                .catch(() => {
                    if (requestId !== requestSequenceRef.current) {
                        return;
                    }

                    alertMessage(alerts, setAlerts, t('There was an error searching prospects'), 'Error');
                })
                .finally(() => {
                    if (requestId !== requestSequenceRef.current) {
                        return;
                    }

                    loadingRequestRef.current = false;
                    setLoading(false);
                });
            return;
        }

        load_prospects(1, 'replace');
    }, [search, filters, refreshKey]);

    useEffect(() => {
        Promise.all([
            read_dolores_growth_users().catch(() => []),
            get_prospect_filter_options().catch(() => ({ sources: [] }))
        ])
            .then(([members, filterOptions]) => {
                setGrowthMembers(members || []);
                setSourceOptions(filterOptions?.sources || []);
            })
            .catch(() => {
                setGrowthMembers([]);
                setSourceOptions([]);
            });
    }, []);

    useEffect(() => {
        setSelectedMergeProspectIds((currentIds) => {
            const visibleProspectIds = new Set(prospects.map((prospect) => `${prospect?._id || ''}`));
            const visibleSelectedIds = currentIds.filter((prospectId) => visibleProspectIds.has(prospectId));

            return visibleSelectedIds.length === currentIds.length ? currentIds : visibleSelectedIds;
        });
    }, [prospects]);

    useEffect(() => {
        const loadMoreTrigger = loadMoreTriggerRef.current;

        if (!loadMoreTrigger || !showLoadMore || loading || loadingMore) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                load_more_prospects();
            }
        }, {
            root: null,
            rootMargin: '20% 0%',
            threshold: 0
        });

        observer.observe(loadMoreTrigger);

        return () => {
            observer.disconnect();
        };
    }, [showLoadMore, loading, loadingMore, pageNo, prospects.length, filters]);

    return (
        <PageSidebar>
            <Alert_ duration={6000} />
            <Toolbar_
                sidebar={sidebar}
                activeOption="leads"
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                onInternalNavigate={navigate}
            />
            {selectedProspect && slider === 'brands' ? (
                <Slider_
                    title={t('Prospect details')}
                    text={t('Done')}
                    icon="Check"
                    onClick={() => save_selected_prospect(selectedProspect, selectedProspectSnapshotRef.current, setSelectedProspect, setProspects, setSlider, alerts, setAlerts, t)}
                    onClose={close_prospect_slider}
                >
                    {canMerge ? (
                        <LeadMergeActions>
                            <Main_Plain
                                text={t('Consolidate brands')}
                                iconLeft="Group"
                                disabled={(selectedProspect.brands || []).filter(brand => brand?._id && !brand.archived).length < 2}
                                onClick={open_consolidate_brands_slider}
                            ></Main_Plain>
                        </LeadMergeActions>
                    ) : null}
                    <Leads_Info
                        selectedLead={selectedProspect}
                        growthMembers={growthMembers}
                        t={t}
                    />
                    <Leads_Brand
                        selectedLead={selectedProspect}
                        setSelectedLead={setSelectedProspect}
                        growthMembers={growthMembers}
                        assigningBrandId={assigningBrandId}
                        canAssignToMe={canAssignToMe}
                        currentSeller={currentSeller}
                        onAssignToMe={assign_selected_brand_to_me}
                    />
                    <Leads_Combined_Timeline selectedLead={selectedProspect} t={t} />
                </Slider_>
            ) : null}
            {slider === 'merge-selected' && mergeProspects.length >= 2 && canMerge ? (
                <Merge_Records
                    entityType="lead"
                    record={{ id: mergeProspects[0]._id, label: read_merge_prospect_label(mergeProspects[0]) }}
                    records={mergeProspects.map((prospect) => ({
                        id: prospect._id,
                        label: read_merge_prospect_label(prospect)
                    }))}
                    lockSurvivor
                    t={t}
                    onClose={close_selected_merge_slider}
                    onMerged={apply_selected_merge}
                ></Merge_Records>
            ) : null}
            {selectedProspect && slider === 'consolidate-brands' && canMerge ? (
                <Consolidate_Brands
                    leadId={selectedProspect._id}
                    brands={selectedProspect.brands || []}
                    t={t}
                    onClose={() => setSlider('brands')}
                    onMerged={result => {
                        selectedProspectSnapshotRef.current = JSON.stringify(result.prospect);
                        setSelectedProspect(result.prospect);
                        setSlider('brands');
                        setRefreshKey(value => value + 1);
                        alertMessage(alerts, setAlerts, t('Brands consolidated'), 'Success');
                    }}
                ></Consolidate_Brands>
            ) : null}
            {slider === 'filters' ? (
                <Slider_
                    title={t('Filters')}
                    text={t('Apply')}
                    onClick={apply_filters}
                    onClose={close_filters_slider}
                >
                    <Lead_Filters
                        t={t}
                        filters={filterDraft}
                        appliedFilters={filters}
                        sourceOptions={sourceOptions.map((source) => ({
                            value: source,
                            label: read_source_label(source, growthMembers)
                        }))}
                        onChange={setFilterDraft}
                        onClear={clear_filters}
                    />
                </Slider_>
            ) : null}
                    <LeadPageContent>
                        <PageTitle cta>
                            <h1>{t('Leads')}</h1>
                            <LeadPageActions>
                                {canMerge ? (
                                    <Toggle_Stat
                                        active={mergeMode}
                                        text={t('Merge mode')}
                                        onClick={toggle_merge_mode}
                                    />
                                ) : null}
                                {canMerge && mergeMode && selectedMergeProspects.length >= 2 ? (
                                    <Main_Plain iconLeft="Group" text={t('Merge')} onClick={open_selected_merge_slider} />
                                ) : null}
                                <Main_Plain iconLeft="Filter" text={filtersButtonText} onClick={open_filters_slider} />
                            </LeadPageActions>
                        </PageTitle>
                        <Filters_>
                            <Input_
                                type="text"
                                placeholder={t('Search prospects')}
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                        </Filters_>
                        {loading ? <Partial_Loader text={t('Loading prospects')} /> : null}
                        <Table>
                            <TR>
                                {canMerge && mergeMode ? <TH title={t('Select leads')}></TH> : null}
                                <TH>{t('A/A')}</TH>
                                <TH>{t('Date')}</TH>
                                <TH>{t('First name')}</TH>
                                <TH>{t('Last name')}</TH>
                                <TH>{t('Username')}</TH>
                                <TH>{t('Brands')}</TH>
                                <TH>{t('Registered')}</TH>
                                <TH>{t('SASS')}</TH>
                                <TH>{t('Source')}</TH>
                            </TR>
                            {prospects.map((prospect, index) => {
                                const sassSummary = read_prospect_sass_summary(prospect);
                                const registered = read_registered_status(prospect);

                                return (
                                    <LeadRow key={prospect?._id} onClick={() => open_prospect_slider(prospect)}>
                                        {canMerge && mergeMode ? (
                                            <LeadMergeSelectionCell onClick={(event) => event.stopPropagation()}>
                                                <Checkbox_
                                                    checked={selectedMergeProspectIds.includes(`${prospect?._id || ''}`)}
                                                    id={`merge-lead-${prospect?._id || index}`}
                                                    text={`${t('Select lead')} ${read_merge_prospect_label(prospect)}`}
                                                    title={`${t('Select lead')} ${read_merge_prospect_label(prospect)}`}
                                                    onChange={() => {}}
                                                    onClick={() => toggle_merge_prospect(prospect?._id)}
                                                />
                                            </LeadMergeSelectionCell>
                                        ) : null}
                                        <TD>{index + 1}</TD>
                                        <TD>{new Date(prospect?.legacyCreatedAt || prospect?.createdAt).toLocaleDateString('el-GR')}</TD>
                                        <TD>{prospect?.firstName}</TD>
                                        <TD>{prospect?.lastName}</TD>
                                        <TD>{prospect?.username}</TD>
                                        <TD>{prospect?.brands?.length || 0}</TD>
                                        <TD>
                                            <LeadRegistrationStatus $registered={registered}>
                                                {registered ? t('Yes') : t('No')}
                                            </LeadRegistrationStatus>
                                        </TD>
                                        <TD>
                                            {sassSummary.available ? (
                                                <Quest_Provider_Counts brand={{ questCounts: sassSummary }} t={t} />
                                            ) : t('N/A')}
                                        </TD>
                                        <TD>
                                            <LeadPageTags>
                                                {prospect?.source ? (
                                                    <LeadPageTag backgroundColor={read_tag_background_color(read_source_label(prospect.source, growthMembers))}>
                                                        <h5>{read_source_label(prospect.source, growthMembers)}</h5>
                                                    </LeadPageTag>
                                                ) : null}
                                            </LeadPageTags>
                                        </TD>
                                    </LeadRow>
                                );
                            })}
                        </Table>
                        {showLoadMore ? (
                            <LeadPageLoadMoreTrigger ref={loadMoreTriggerRef}>
                                {loadingMore ? <Partial_Loader text={t('Loading prospects')} /> : null}
                            </LeadPageLoadMoreTrigger>
                        ) : null}
                    </LeadPageContent>
        </PageSidebar>
    );
}
